import { FC, useCallback } from 'react';

import SectionContainer from '@common/components/SectionContainer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useCheckForArrivalDate } from '@common/hooks';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useSpaSearchComplete } from '@features/JourneyPickerV2/useSpaSearchComplete';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';
import { buildCategoryOptions } from '@features/spa/utils/buildCategoryOptions';
import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';

import { SpaCategoryListTemplate, SpaListTemplate } from 'voyager';

import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { mapCategoriesToCards } from '@features/spa/utils/mapCategoriesToCards';

import { useGetSpaTreatmentsPage } from '@features/spa/hooks/useGetSpaTreatmentsPage';
import { useSpaEntityFilter } from '@features/spa/hooks/useSpaEntityFilter';
import { useSpaTreatmentsList } from './hooks/useSpaTreatmentsList';
import { SpaTreatmentsV2Props } from './SpaTreatmentsV2.types';

export const SpaTreatmentsV2: FC<SpaTreatmentsV2Props> = ({
  property,
  journeyParams,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_TREATMENT_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    guests: journeyParams?.spaAdults,
  });

  const handleSearchComplete = useSpaSearchComplete();

  const { listItems, uncategorizedItems, isInitialLoading, isRefetching } = useSpaTreatmentsList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    categoryFilterValue: filter.categoryFilterValue,
    date: filter.appliedDateString,
    guests: filter.guests,
  });

  const { data: treatmentsCategories = [], isFetching: isFetchingCategories } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const isTreatmentCategoryViewVisible = spaSettings?.isTreatmentCategoryViewVisible ?? false;
  const showCategoryView = isTreatmentCategoryViewVisible && treatmentsCategories.length > 1;

  const { data: treatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const fallbackImageUrl = treatmentsPage?.heroBackgroundImage?.url;

  const noCategoryLabel = t('spa.noCategory');

  const categoryOptions = buildCategoryOptions(
    treatmentsCategories,
    t('spa.package.allCategories'),
    uncategorizedItems.length > 0 ? noCategoryLabel : undefined
  );

  const handleSelectTreatment = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
  };

  const handleSelectCategory = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_CATEGORY_DETAIL, query: { id } });
  };

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const titleLabel = t('app.spaTreatments');

  return (
    <EntitiesListingWrapper>
      {!showCategoryView && (
        <JourneyPickerOuterWrapper>
          <JourneyPickerInnerWrapper>
            <JourneyPickerV2
              dateMode="single"
              dateContext="spa"
              showGuests
              categories={navCategories}
              category="spa-treatments"
              onCategoryChange={handleNavCategoryChange}
              onSearchComplete={handleSearchComplete}
            />
          </JourneyPickerInnerWrapper>
        </JourneyPickerOuterWrapper>
      )}
      <SectionContainer>
        {showCategoryView ? (
          <SpaCategoryListTemplate
            categories={mapCategoriesToCards(
              treatmentsCategories,
              'treatments',
              uncategorizedItems.length > 0
                ? { label: noCategoryLabel, imageUrl: fallbackImageUrl }
                : undefined
            )}
            onSelectCategory={handleSelectCategory}
            isLoading={isFetchingCategories}
            titleLabel={titleLabel}
            fromPriceLabel={t('common.from')}
            noResultsLabel={t('spa.noResults')}
            noImageLabel={t('spa.noImage')}
          />
        ) : (
          <>
            <Spinner showDelayMessage isActive={isRefetching} />
            <SpaListTemplate
              items={listItems}
              onSelectItem={handleSelectTreatment}
              isLoading={isInitialLoading}
              categoryOptions={categoryOptions}
              selectedCategory={filter.selectedCategory || ''}
              onCategoryChange={filter.handleDirectCategoryChange}
              titleLabel={titleLabel}
              categoryLabel={t('common.category')}
              fromPriceLabel={t('common.from')}
              soldOutLabel={t('common.unavailableForSearch')}
              editSearchLabel={t('common.editSearch')}
              onEditSearch={handleEditSearch}
              noResultsLabel={t('spa.noResults')}
              noImageLabel={t('spa.noImage')}
              formatGuests={(min, max) =>
                min === max
                  ? t('common.numberOfGuests', { guestsAmount: min })
                  : t('common.guestRange', { from: min, to: max })
              }
            />
          </>
        )}
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
